import { createSelector, createSlice } from "@reduxjs/toolkit";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { RootState } from "../store";
import type { TFetchStatus } from "../types";
import type { CurrentCompanyGroup, ICompanyGroup, ICompanyGroupMember } from ".";
import {
    assignCompanyToGroup,
    createCompanyGroup,
    deleteCompanyGroup,
    fetchCompanyGroups,
    fetchCurrentCompanyGroup,
    fetchGroupCompanies,
    fetchSharedFields,
    unassignCompanyFromGroup,
    updateCompanyGroup,
    updateSharedFields,
} from "./company-groups-use-cases";

export interface CompanyGroupsState {
    currentGroup?: CurrentCompanyGroup;
    currentGroupStatus: TFetchStatus;
    groups: ICompanyGroup[];
    groupsStatus: TFetchStatus;
    members: ICompanyGroupMember[];
    membersStatus: TFetchStatus;
    selectedGroupId: number | null;
    mutationStatus: TFetchStatus;
    sharedFields: string[];
    sharedFieldsCatalog: string[];
    sharedFieldsStatus: TFetchStatus;
}

export const initialState: CompanyGroupsState = {
    currentGroup: undefined,
    currentGroupStatus: "idle",
    groups: [],
    groupsStatus: "idle",
    members: [],
    membersStatus: "idle",
    selectedGroupId: null,
    mutationStatus: "idle",
    sharedFields: [],
    sharedFieldsCatalog: [],
    sharedFieldsStatus: "idle",
};

export const companyGroupSelector = (state: RootState) => state.companyGroups;
export const selectSiblingCompanies = createSelector(
    [
        (state: RootState) => companyGroupSelector(state).currentGroup?.sameSiretCompanies ?? [],
        (state: RootState) => companyGroupSelector(state).currentGroup?.companies ?? [],
        (state: RootState) => state.settings.companySettings?.id,
    ],
    (sameSiretCompanies, companies, companyId) => {
        return companies.filter(({ id }) => sameSiretCompanies.includes(id) && id !== companyId);
    },
);

export const companyGroupsSlice = createSlice({
    name: "companyGroups",
    initialState,
    reducers: {
        resetMutationStatus(state) {
            state.mutationStatus = "idle";
        },
        clearSelectedGroup(state) {
            state.selectedGroupId = null;
            state.members = [];
            state.membersStatus = "idle";
            state.sharedFields = [];
            state.sharedFieldsStatus = "idle";
        },
    },
    extraReducers: (builder) => {
        // Fetch groups
        builder.addCase(fetchCompanyGroups.pending, (state) => {
            state.groupsStatus = "pending";
        });
        builder.addCase(fetchCompanyGroups.fulfilled, (state, action) => {
            state.groups = action.payload;
            state.groupsStatus = "succeeded";
        });
        builder.addCase(fetchCompanyGroups.rejected, (state) => {
            state.groupsStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.fetch.error" });
        });

        // Create
        builder.addCase(createCompanyGroup.pending, (state) => {
            state.mutationStatus = "pending";
        });
        builder.addCase(createCompanyGroup.fulfilled, (state, action) => {
            state.groups.push({
                id: action.payload.id,
                name: action.payload.name,
                createdAt: new Date().toISOString(),
                modifiedAt: null,
            });
            state.mutationStatus = "succeeded";
            toast.success(`${t("CompanyGroups.GroupCreated")}`);
        });
        builder.addCase(createCompanyGroup.rejected, (state) => {
            state.mutationStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.create.error" });
        });

        // Update
        builder.addCase(updateCompanyGroup.pending, (state) => {
            state.mutationStatus = "pending";
        });
        builder.addCase(updateCompanyGroup.fulfilled, (state, action) => {
            const index = state.groups.findIndex((g) => g.id === action.payload.id);
            if (index !== -1) state.groups[index] = action.payload;
            state.mutationStatus = "succeeded";
            toast.success(`${t("CompanyGroups.GroupUpdated")}`);
        });
        builder.addCase(updateCompanyGroup.rejected, (state) => {
            state.mutationStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.update.error" });
        });

        // Delete
        builder.addCase(deleteCompanyGroup.fulfilled, (state, action) => {
            state.groups = state.groups.filter((g) => g.id !== action.payload);
            if (state.selectedGroupId === action.payload) {
                state.selectedGroupId = null;
                state.members = [];
            }
            toast.success(`${t("CompanyGroups.GroupDeleted")}`);
        });
        builder.addCase(deleteCompanyGroup.rejected, () => {
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.delete.error" });
        });

        // Fetch members
        builder.addCase(fetchGroupCompanies.pending, (state) => {
            state.membersStatus = "pending";
        });
        builder.addCase(fetchGroupCompanies.fulfilled, (state, action) => {
            state.selectedGroupId = action.payload.groupId;
            state.members = action.payload.members;
            state.membersStatus = "succeeded";
        });
        builder.addCase(fetchGroupCompanies.rejected, (state) => {
            state.membersStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.members.error" });
        });

        // Assign company
        builder.addCase(assignCompanyToGroup.fulfilled, () => {
            toast.success(`${t("CompanyGroups.CompanyAdded")}`);
        });
        builder.addCase(assignCompanyToGroup.rejected, (_state, action) => {
            const status = (action.payload as { status?: number } | undefined)?.status;
            if (status === 409) {
                toast.error(`${t("CompanyGroups.CompanyAlreadyInGroup")}`, {
                    toastId: "companyGroups.assign.conflict",
                });
            } else {
                toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.assign.error" });
            }
        });

        // Unassign company
        builder.addCase(unassignCompanyFromGroup.fulfilled, () => {
            toast.success(`${t("CompanyGroups.CompanyRemoved")}`);
        });
        builder.addCase(unassignCompanyFromGroup.rejected, () => {
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.unassign.error" });
        });

        // Fetch shared fields
        builder.addCase(fetchSharedFields.pending, (state) => {
            state.sharedFieldsStatus = "pending";
        });
        builder.addCase(fetchSharedFields.fulfilled, (state, action) => {
            state.sharedFields = action.payload.fields;
            state.sharedFieldsCatalog = action.payload.catalog;
            state.sharedFieldsStatus = "succeeded";
        });
        builder.addCase(fetchSharedFields.rejected, (state) => {
            state.sharedFieldsStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.sharedFields.error" });
        });

        // Update shared fields
        builder.addCase(updateSharedFields.pending, (state) => {
            state.sharedFieldsStatus = "pending";
        });
        builder.addCase(updateSharedFields.fulfilled, (state, action) => {
            state.sharedFields = action.payload.fields;
            state.sharedFieldsStatus = "succeeded";
            toast.success(`${t("CompanyGroups.SharedFieldsUpdated")}`);
        });
        builder.addCase(updateSharedFields.rejected, (state) => {
            state.sharedFieldsStatus = "failed";
            toast.error(`${t("ServerError.InternalServerError")}`, {
                toastId: "companyGroups.sharedFields.save.error",
            });
        });

        // Get current company group
        builder.addCase(fetchCurrentCompanyGroup.pending, (state) => {
            state.currentGroupStatus = "pending";
        });
        builder.addCase(fetchCurrentCompanyGroup.fulfilled, (state, action) => {
            state.currentGroupStatus = "fulfilled";
            state.currentGroup = action.payload;
        });
        builder.addCase(fetchCurrentCompanyGroup.rejected, (state, error) => {
            state.currentGroupStatus = "rejected";
            console.error(error);
            toast.error(`${t("ServerError.InternalServerError")}`, { toastId: "companyGroups.getCurrentGroup.error" });
        });
    },
});

export const { resetMutationStatus, clearSelectedGroup } = companyGroupsSlice.actions;

export default companyGroupsSlice.reducer;
